import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { isEmpty } from 'lodash';
import { FC, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Cards from 'src/components/Cards';
import Button from 'src/components/design-system/Button';
import useWindowSize from 'src/hooks/useWindowSize';
import Dialog from '../../components/design-system/Dialog';
import useRecallApi from '../../services/useRecallApi';

import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import PopUp from 'src/components/design-system/PopUp';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';
import useInvoiceButtonState from './useInvoiceButtonState';

interface IInformationComponent {
  content: string[];
}
const InformationComponent: React.FC<IInformationComponent> = ({ content }) => (
  <Cards title="Information" styleType="information w-full" fontWeight="bold">
    <ul style={{ width: '300px', textAlign: 'left' }}>
      {content.map((text, index) => (
        <li key={index}>{text}</li>
      ))}
    </ul>
  </Cards>
);

interface RecallProps {}

const Recall: FC<RecallProps> = () => {
  const { width } = useWindowSize();
  const invoice = useContextSelector(InvoiceContext, (value) => value.invoice);
  const refreshPage = useContextSelector(InvoiceContext, (value) => value.refreshPage);
  const isInvoiceActionButtonDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceActionButtonDisabled
  );
  const getMessages = useContextSelector(InvoiceContext, (value) => value.getMessages);

  const { receiptId } = invoice;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [modalStepCount, setModalStepCount] = useState(0);
  const { recallInvoice, recallCallLoading } = useRecallApi();
  const [message, buttonState] = useInvoiceButtonState('Recall');

  const openModal = useCallback(() => {
    setModalStepCount(0);
    setIsOpenModal(true);
  }, []);

  const closeModal = useCallback(() => setIsOpenModal(false), []);

  const InformationMemoized = useMemo(() => {
    const infoList: string[] = [];
    const { totalCostAmt } = invoice;
    if (totalCostAmt === 0) {
      infoList.push('This document has a zero dollar balance.');
    }
    infoList.push('This action cannot be reversed.');
    return <InformationComponent content={infoList} />;
  }, [invoice]);

  const RecallModal = useMemo(
    () => (
      <div className="px-2 font-bold">
        {InformationMemoized}
        <br />
        <p className="font-bold">Continue to Recall?</p>
      </div>
    ),
    [InformationMemoized]
  );
  const handleRecall = useCallback(async () => {
    try {
      const response = await recallInvoice({ invoiceIds: [receiptId] });
      if (!isEmpty(response?.unProcessed)) {
        toast.error(`Document recall unsuccessful`);
      } else {
        setModalStepCount(1);
      }
    } catch (e) {
      console.error(e);
      toast.error('Something went wrong');
    } finally {
      refreshPage();
    }
  }, [recallInvoice, receiptId, refreshPage]);

  const modalSteps = useMemo(
    () => [
      {
        title: 'Recall',
        content: RecallModal,
        isConfirmationModal: false,
        clickFunction: handleRecall,
      },
      {
        title: 'Document Recalled Successfully',
        content: <b>Document Recalled Successfully</b>,
        isConfirmationModal: true,
      },
    ],
    [RecallModal, handleRecall]
  );

  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper type="Error" contents={[...getMessages(buttonState, message)]} />
          </div>
        }
        show={isInvoiceActionButtonDisabled(buttonState)}
      >
        <Button
          onClick={openModal}
          color="error"
          loading={recallCallLoading}
          buttonState={buttonState}
          disabled={isInvoiceActionButtonDisabled(buttonState)}
        >
          Recall
        </Button>
      </PopUp>

      <Dialog
        width={width > 1480 ? '35vw' : '600px'}
        onClose={closeModal}
        open={isOpenModal}
        title={modalSteps?.[modalStepCount]?.title}
        id="modal"
      >
        {modalSteps?.[modalStepCount]?.content}
        <DialogActionsBar>
          <div className="flex items-center justify-end">
            {!modalSteps?.[modalStepCount]?.isConfirmationModal && (
              <div className="flex items-center gap-1 mr-2">
                <Button color="info" onClick={modalSteps?.[modalStepCount]?.clickFunction}>
                  {modalSteps?.[modalStepCount].title}
                </Button>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Button color="info" onClick={closeModal}>
                {modalSteps?.[modalStepCount]?.isConfirmationModal ? 'OK' : 'Cancel'}
              </Button>
            </div>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default Recall;
